/* eslint-disable react/function-component-definition */
/* eslint-disable react/jsx-no-useless-fragment */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { schemaLoader } from '@civic/dsr';
import * as R from 'ramda';
import Container from '../common/components/Container/Container';
import Button, { ButtonStyle } from '../common/components/Button/Button';
import ButtonContainer from '../common/components/Button/ButtonContainer';
import TitleContainer from '../common/components/TitleContainer/TitleContainer';
import withRouteAnalytics from '../services/analytics/withRouteAnalytics';
import { Screens } from '../services/analytics/screens';
import { useCredentialStore } from '../providers/CredentialStoreContext';
import { Routes } from '../config/routes';
import { documentAttributesFromCredential, emailAddressFromCredential } from '../utils/credentials';
import {
  EmailCredentialSubject,
  FailedConstraint,
  IDDocumentCredentialSubject,
  ReuseStatus,
  VerifiableCredential,
  VerifiableCredentialExtended,
} from '../common/types';
import SVGErrorDot from '../common/assets/svg/icons/SVGErrorDot';
import Log from '../common/logging';
import SchemaLoader from '../services/credential/schemaService';
import schemaMapping from '../utils/jsonSchemaLoader';
import { setStorageItemForNetwork, StorageKey } from '../utils/storage';
import { FlowConfigContext } from '../providers/FlowConfigContext';

interface CredentialListItemProps {
  credential: VerifiableCredentialExtended<IDDocumentCredentialSubject>;
  updateChosenCredentials: (cred: VerifiableCredential) => void;
  selected: boolean;
  disabled: boolean;
}

const CredentialListItem: React.FC<CredentialListItemProps> = ({
  credential,
  updateChosenCredentials,
  selected,
  disabled,
}) => {
  const { t } = useTranslation();
  const documentAttributes = documentAttributesFromCredential(
    credential as VerifiableCredential<IDDocumentCredentialSubject>,
    t,
  );
  const isValid = !!credential.validation?.isValid;
  const documentExpiresLabel =
    documentAttributes.expiry !== ''
      ? `${t('screens.chooseCredentials.documentExpires')} - ${documentAttributes.expiry}`
      : t('screens.chooseCredentials.documentNoExpiry');
  const defaultConstraintFailureLabel = t('screens.credentialValidation.failedConstraints.default');
  return (
    <li
      data-testid={`CREDENTIAL_LIST_ITEM_${credential.id}`}
      className={`p-2 mb-2 mx-auto w-2/3 ${!isValid ? `invalid cursor-default text-textNeutral` : 'cursor-pointer'} ${
        selected ? 'selected border border-primary rounded-lg' : 'border border-border rounded-lg'
      }`}
    >
      <button
        className={`flex flex-col mx-auto ${!isValid ? `invalid cursor-default text-textNeutral` : 'cursor-pointer'}`}
        type="button"
        onClick={() => updateChosenCredentials(credential)}
        disabled={disabled}
        key={credential.id}
      >
        <span className={`w-full flex flex-row ${!isValid ? `invalid text-textNeutral` : 'cursor-pointer'}`}>
          {!isValid && (
            <span className="flex flex-row mr-1 items-center">
              <SVGErrorDot />
            </span>
          )}
          <span className="flex flex-row">{documentAttributes.title}</span>
        </span>
        <span className="w-full flex flex-col text-sm text-textNeutral items-center">
          {credential.validation?.isValid && (
            <span data-testid={`DOCUMENT_EXPIRY_${credential.id}`} className="w-full flex flex-col">
              {documentExpiresLabel}
            </span>
          )}
          {credential.validation?.failedConstraints.map((constraint: FailedConstraint) => {
            let useLabelPath = constraint.constraint.path;
            if (useLabelPath === '$or' && Array.isArray(constraint.constraint.is)) {
              // the constraint object is in an aggregate array of objects where the path is the key
              useLabelPath = Object.keys((constraint.constraint.is as Record<string, unknown>[])?.[0])?.[0] || '';
            }
            Log.debug(`${credential.id} failed constraint:`, {
              constraint,
              label: `screens.credentialValidation.failedConstraints.${useLabelPath}`,
            });
            return (
              <span
                key={`FAILED_CONSTRAINT_${constraint.credentialId}_${useLabelPath}`}
                data-testid={`FAILED_CONSTRAINT_PATH_${useLabelPath}`}
                className="w-full flex flex-col"
              >
                {t(`screens.credentialValidation.failedConstraints.${useLabelPath}`, {
                  defaultValue: defaultConstraintFailureLabel,
                })}
              </span>
            );
          })}
        </span>
      </button>
    </li>
  );
};

function ChooseCredentialsScreen(): JSX.Element | null {
  schemaLoader.addLoader(new SchemaLoader(schemaMapping));
  const { setReuseStatus, getDocumentCredentials, setChosenCredentialIds, emailCredentials } = useCredentialStore();
  const [localChosenCredentials, setLocalChosenCredentials] = useState<VerifiableCredential[]>([]);
  const [documentCredentials, setDocumentCredentials] = useState<
    VerifiableCredentialExtended<IDDocumentCredentialSubject>[]
  >([]);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { networkAddress } = useContext(FlowConfigContext);

  const localChosenCredentialIds = useMemo(
    () => localChosenCredentials.map((cred) => cred.id),
    [localChosenCredentials],
  );
  const onConfirm = useCallback(async () => {
    // Store the credentialExpired flag in local storage so the fees screen can display the correct fees
    setStorageItemForNetwork( StorageKey.CredentialExpired, networkAddress, 'true');
    setChosenCredentialIds(localChosenCredentialIds);
    setReuseStatus(ReuseStatus.selected);
    navigate(Routes.VALIDATION_NEXT_STEP);
  }, [localChosenCredentialIds, navigate, setChosenCredentialIds, setReuseStatus]);

  const onConfirmUseAlternative = useCallback(async () => {
    setChosenCredentialIds([]);
    setReuseStatus(ReuseStatus.rejected);
    navigate(Routes.VALIDATION_NEXT_STEP);
  }, [navigate, setChosenCredentialIds, setReuseStatus]);

  const updateChosenCredentials = useCallback(
    (cred: VerifiableCredential) => {
      // remove any credentials of the same type from localChosenCredentials if they have a different id than the input
      setLocalChosenCredentials([...localChosenCredentials.filter((c) => c.identifier !== cred.identifier), cred]);
    },
    [localChosenCredentials],
  );

  useEffect(() => {
    getDocumentCredentials().then((useDocumentCredentials) => {
      setDocumentCredentials(useDocumentCredentials);
      if (emailCredentials && useDocumentCredentials) {
        const initialCredentials = [];
        initialCredentials.push(emailCredentials[0]);
        const validDocumentCredentials = useDocumentCredentials.filter((cred) => !!cred.validation?.isValid);

        if (validDocumentCredentials.length > 0) {
          initialCredentials.push(validDocumentCredentials[0]);
        }
        setLocalChosenCredentials(initialCredentials);
      }
    });
  }, [emailCredentials, getDocumentCredentials]);

  const optionFromEmail = (emailCred: VerifiableCredential<EmailCredentialSubject>) => ({
    value: emailCred.id,
    label: emailAddressFromCredential(emailCred),
    credential: emailCred,
  });

  const hasValidDocumentCredentials = useMemo(
    () => documentCredentials?.some((cred) => !!cred.validation?.isValid),
    [documentCredentials],
  );

  return (
    <Container>
      <>
        <TitleContainer center>
          <h1 className="font-normal mb-4" data-testid="CHOOSE_STORED_CREDENTIALS">
            <span>🔒 </span>
            {t('screens.chooseCredentials.title')}
          </h1>
        </TitleContainer>
        {emailCredentials && emailCredentials.length > 0 && (
          <div className="mb-4 w-2/3 text-center">
            {emailCredentials.length === 1 ? (
              <span data-testid="SINGLE_EMAIL_CREDENTIAL">{emailAddressFromCredential(emailCredentials[0])}</span>
            ) : (
              <div data-testid="MULTI_EMAIL_CREDENTIALS">
                <Select
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      boxShadow: state.isFocused ? 'none' : baseStyles.boxShadow,
                      backgroundColor: 'rgba(var(--color-input-field-background))',
                      borderWidth: '1px',
                      borderColor: 'rgba(var(--color-input-field-border))',
                      '&:hover': {
                        borderColor: 'rgba(var(--color-input-field-border))',
                      },
                      borderRadius: '8px',
                    }),
                    menu: (baseStyles) => ({
                      ...baseStyles,
                      backgroundColor: 'rgb(var(--default-background))',
                      borderWidth: '0.48px',
                      borderColor: 'rgba(var(--color-input-field-border))',
                      boxShadow: 'none',
                      borderRadius: '8px',
                    }),
                    option: (baseStyles, state) => ({
                      ...baseStyles,
                      color: state.isSelected ? 'rgb(var(--color-text-button))' : 'rgb(var(--color-text-headline))',
                      backgroundColor: state.isSelected
                        ? 'rgba(var(--color-primary))'
                        : 'rgba(var(--color-input-field-background))',
                      '&:hover': {
                        backgroundColor: state.isSelected
                          ? 'rgba(var(--color-primary))'
                          : 'rgba(var(--color-primary-transparent))',
                      },
                    }),
                    indicatorSeparator: (baseStyles) => ({
                      ...baseStyles,
                      backgroundColor: 'rgba(var(--color-input-field-background))',
                    }),
                    valueContainer: (baseStyles) => ({
                      ...baseStyles,
                      color: 'rgb(var(--color-text-headline))',
                    }),
                    placeholder: (baseStyles) => ({
                      ...baseStyles,
                      color: 'rgb(var(--color-text-headline))',
                    }),
                    singleValue: (baseStyles) => ({
                      ...baseStyles,
                      color: 'rgb(var(--color-text-headline))',
                    }),
                    multiValue: (baseStyles) => ({
                      ...baseStyles,
                      backgroundColor: 'rgba(var(--color-input-field-background))',
                      borderRadius: 'var(--border-radius-md)',
                    }),
                    multiValueLabel: (baseStyles) => ({
                      ...baseStyles,
                      color: 'rgb(var(--color-text-neutral))',
                    }),
                    multiValueRemove: (baseStyles) => ({
                      ...baseStyles,
                      backgroundColor: 'rgba(var(--color-input-field-background))',
                      borderRadius: 'var(--border-radius-full)',
                      '&:hover': {
                        backgroundColor: 'rgba(var(--color-input-field-background))',
                      },
                    }),
                    clearIndicator: (baseStyles) => ({
                      ...baseStyles,
                      color: 'rgb(var(--color-primary))',
                      '&:hover': {
                        color: 'rgb(var(--color-primary))',
                      },
                    }),
                    dropdownIndicator: (baseStyles) => ({
                      ...baseStyles,
                      color: 'rgb(var(--color-primary))',
                      '&:hover': {
                        color: 'rgb(var(--color-primary))',
                      },
                    }),
                  }}
                  isSearchable={false}
                  isMulti={false}
                  defaultValue={optionFromEmail(emailCredentials[0] as VerifiableCredential<EmailCredentialSubject>)}
                  options={(emailCredentials as VerifiableCredential<EmailCredentialSubject>[]).map(optionFromEmail)}
                  onChange={(e) => e && updateChosenCredentials(e.credential)}
                />
              </div>
            )}
          </div>
        )}

        {documentCredentials && documentCredentials.length > 0 && (
          <>
            <div>{t('screens.chooseCredentials.documentLabel')}:</div>
            <ul data-testid="DOCUMENT_CREDENTIALS" className="flex w-full flex-col">
              {documentCredentials.map((credential) => (
                <CredentialListItem
                  key={credential.id}
                  data-testid={`DOCUMENT_CREDENTIAL_${credential.id}`}
                  credential={credential}
                  updateChosenCredentials={updateChosenCredentials}
                  selected={localChosenCredentials.map(R.prop('id')).includes(credential.id)}
                  disabled={documentCredentials.length === 1 || !credential.validation?.isValid}
                />
              ))}
            </ul>
          </>
        )}

        {hasValidDocumentCredentials ? (
          <>
            <ButtonContainer bottomMargin="mb-2 mt-4">
              <Button
                style={ButtonStyle.DEFAULT}
                disabled={localChosenCredentials.length === 0}
                loading={false}
                dataTestid="CONFIRM_USE_CREDENTIALS_BUTTON"
                onClick={() => onConfirm()}
              >
                <span>{t('screens.chooseCredentials.useCredentialsButton')}</span>
              </Button>
            </ButtonContainer>
            <ButtonContainer bottomMargin="mb-2 mt-4">
              <Button
                style={ButtonStyle.BACK}
                disabled={false}
                loading={false}
                dataTestid="CONFIRM_USE_ALTERNATIVE_CREDENTIALS_BUTTON"
                onClick={() => onConfirmUseAlternative()}
              >
                <span>{t('screens.chooseCredentials.useAlternativeCredentialsButton')}</span>
              </Button>
            </ButtonContainer>
          </>
        ) : (
          <ButtonContainer bottomMargin="mb-2 mt-4">
            <Button
              style={ButtonStyle.DEFAULT}
              disabled={false}
              loading={false}
              dataTestid="CONFIRM_USE_ALTERNATIVE_CREDENTIALS_BUTTON"
              onClick={() => onConfirmUseAlternative()}
            >
              <span>{t('screens.chooseCredentials.useAlternativeCredentialsButton')}</span>
            </Button>
          </ButtonContainer>
        )}
      </>
    </Container>
  );
}
export default withRouteAnalytics(ChooseCredentialsScreen, Screens.CHOOSE_CREDENTIALS);
